import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  message,
  Descriptions,
  Alert,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { crearNovedad } from "../../services/novedad_participante/novedadParticipanteService";
import cargarDeptosMunicipios from "../../services/departMunic/deptosMunicipiosServices";

const { TextArea } = Input;

/**
 * Crear novedad. `participanteFijo` (documento, nombre, municipioId,
 * municipioNombre) llega precargado y bloqueado cuando se abre desde una
 * fila de /reporteRIT; si viene vacío (módulo standalone Novedades), el
 * municipio/documento/nombre son campos editables del propio formulario.
 */
const NovedadFormModal = ({ open, onClose, participanteFijo = null, onCreada }) => {
  const [form] = Form.useForm();
  const [enviando, setEnviando] = useState(false);
  const [archivos, setArchivos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [conflicto, setConflicto] = useState(null);

  const esFijo = Boolean(participanteFijo);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setArchivos([]);
    setConflicto(null);

    if (!esFijo && !municipios.length) {
      setLoadingMunicipios(true);
      cargarDeptosMunicipios()
        .then((res) => {
          const opciones = (res?.data || []).flatMap((depto) =>
            (depto.municipios || []).map((m) => ({
              value: m.oid_municipio,
              label: `${m.nombre_municipio} (${depto.nombre_departamento})`,
            }))
          );
          setMunicipios(opciones);
        })
        .catch(() => message.error("No se pudo cargar el listado de municipios"))
        .finally(() => setLoadingMunicipios(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const uploadProps = {
    multiple: true,
    showUploadList: true,
    fileList: archivos,
    beforeUpload: (file) => {
      setArchivos((prev) => [...prev, file]);
      return false;
    },
    onRemove: (file) => {
      setArchivos((prev) => prev.filter((f) => f.uid !== file.uid));
    },
  };

  const handleSubmit = async () => {
    try {
      const valores = await form.validateFields();
      setEnviando(true);
      setConflicto(null);

      const formData = new FormData();
      const municipioSeleccionado = esFijo
        ? participanteFijo.municipioNombre
        : municipios.find((m) => m.value === valores.municipio_id)?.label?.split(" (")[0];
      const municipioIdFinal = esFijo ? participanteFijo.municipioId : valores.municipio_id;
      const documentoFinal = esFijo ? participanteFijo.documento : valores.documento_identificacion;
      const nombreFinal = esFijo ? participanteFijo.nombre : valores.nombre_completo;

      formData.append("municipio", municipioSeleccionado || "");
      formData.append("documento_identificacion", documentoFinal);
      formData.append("nombre_completo", nombreFinal || "");
      formData.append("municipio_id", municipioIdFinal);
      formData.append("codigo_dane_municipio", (esFijo && participanteFijo.codigoDane) || "");
      formData.append("tipo_novedad", valores.tipo_novedad);
      formData.append("descripcion", valores.descripcion);
      formData.append("fecha_novedad", valores.fecha_novedad.format("YYYY-MM-DD"));
      archivos.forEach((file) => formData.append("soportes", file));

      const creada = await crearNovedad(formData);
      message.success("Novedad registrada correctamente");
      onCreada?.(creada);
      onClose();
    } catch (error) {
      if (error?.response?.status === 409) {
        setConflicto(error.response.data);
        return;
      }
      if (error?.errorFields) return; // error de validación del form, ya mostrado inline
      console.error(error);
      message.error("Error al registrar la novedad");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Agregar novedad"
      width={640}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={enviando}
      onOk={handleSubmit}
      destroyOnClose
    >
      {conflicto && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Ya existe una novedad activa para este participante"
          description="Debe resolverla o anularla antes de registrar una nueva."
        />
      )}

      {esFijo && (
        <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Municipio">{participanteFijo.municipioNombre}</Descriptions.Item>
          <Descriptions.Item label="Documento">{participanteFijo.documento}</Descriptions.Item>
          <Descriptions.Item label="Nombre">{participanteFijo.nombre}</Descriptions.Item>
        </Descriptions>
      )}

      <Form form={form} layout="vertical">
        {!esFijo && (
          <>
            <Form.Item
              name="municipio_id"
              label="Municipio"
              rules={[{ required: true, message: "Selecciona el municipio" }]}
            >
              <Select
                showSearch
                loading={loadingMunicipios}
                optionFilterProp="label"
                placeholder="Selecciona el municipio"
                options={municipios}
              />
            </Form.Item>
            <Form.Item
              name="documento_identificacion"
              label="Cédula"
              rules={[{ required: true, message: "Ingresa la cédula" }]}
            >
              <Input placeholder="Número de documento" inputMode="numeric" />
            </Form.Item>
            <Form.Item name="nombre_completo" label="Nombre completo">
              <Input placeholder="Nombre del participante" />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="tipo_novedad"
          label="Tipo de novedad"
          rules={[{ required: true, message: "Describe el tipo de novedad" }]}
        >
          <Input placeholder="Ej: Traslado de municipio, No ubicado, Fallecido..." />
        </Form.Item>

        <Form.Item
          name="descripcion"
          label="Descripción"
          rules={[{ required: true, message: "Ingresa la descripción" }]}
        >
          <TextArea rows={4} placeholder="Detalle de la novedad" />
        </Form.Item>

        <Form.Item
          name="fecha_novedad"
          label="Fecha de la novedad"
          rules={[{ required: true, message: "Selecciona la fecha" }]}
          initialValue={dayjs()}
        >
          <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
        </Form.Item>

        <Form.Item label="Soporte(s)">
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Arrastra archivos aquí o haz clic para seleccionar</p>
            <p className="ant-upload-hint">Se permiten uno o varios archivos</p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NovedadFormModal;
